import { Modal, Button, Radio, DatePicker } from "antd"
import { useEffect, useState } from "react"
import { ScheduledEvent } from "../../scheduleApi"
import type { Dayjs } from "dayjs"
import dayjs from "dayjs"
import { red } from "@ant-design/colors"
import { useRouteByIdQuery } from '../../../RouteManagement/routingQueries'

export type ApplyChangesOption = "this-event" | "this-and-future" | "selected-dates"

type Props = {
  open: boolean
  onClose: () => void
  onConfirm: (option: ApplyChangesOption, dateRange?: [Dayjs, Dayjs] | null) => void
  event?: ScheduledEvent | null
  title?: string
  subtitle?: string
  allowFutureForRoute?: boolean
  allowPastDates?: boolean
}

const ScheduledEventsChangesModal = ({ open, onClose, onConfirm, event, title: titleOverride, subtitle: subtitleOverride, allowFutureForRoute = false, allowPastDates = false }: Props) => {
  const [selectedOption, setSelectedOption] = useState<ApplyChangesOption>("this-event")
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null)
  const routeQuery = useRouteByIdQuery(event?.routeId)

  useEffect(() => {
    if (open) {
      setSelectedOption("this-event")
      setDateRange(null)
    }
  }, [open])

  const handleConfirm = () => {
    if (selectedOption === "selected-dates" && !dateRange) {
      return
    }
    onConfirm(selectedOption, dateRange)
    onClose()
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div>
          <div style={{ fontSize: "18px", fontWeight: "600", marginBottom: "4px" }}>
            {titleOverride ?? event?.jobName}
          </div>
          <div style={{ fontSize: "14px", color: "#666", fontWeight: "normal" }}>
            {subtitleOverride ?? (
              <>
                {event?.customerDisplayName} {event?.customerAddress1 || event?.customerCity || event?.customerState || event?.customerZip ? '|' : ''} {event?.customerAddress1 ? event?.customerAddress1 + ',' : ''} {event?.customerCity ? event?.customerCity + ',' : ''} {event?.customerState ? event?.customerState + ',' : ''} {event?.customerZip ? event?.customerZip : ''}
              </>
            )}
          </div>
        </div>
      }
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={selectedOption === "selected-dates" && !dateRange}
          >
            Save
          </Button>
        </div>
      }
      width={600}
      centered
    >
      <div style={{ padding: "16px 0" }}>
        <div style={{
          marginBottom: "24px",
          color: "#595959",
          fontSize: "12px",
        }}>
          Apply Changes To
        </div>

        <Radio.Group
          value={selectedOption}
          onChange={(e) => setSelectedOption(e.target.value)}
          style={{ width: "100%" }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <Radio value="this-event" style={{ fontSize: "14px" }}>
              This event
            </Radio>
            <div>
              <Radio value="selected-dates" style={{ fontSize: "14px" }}>
                This and selected dates
              </Radio>
              {selectedOption === "selected-dates" && (
                <div style={{ marginTop: "12px", marginLeft: "24px" }}>
                  <DatePicker.RangePicker
                    placeholder={["Start", "End"]}
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
                    style={{ width: "100%" }}
                    disabledDate={allowPastDates ? undefined : (current) => current < dayjs().startOf('day')}
                  />
                </div>
              )}
            </div>
            <Radio value="this-and-future" style={{ fontSize: "14px" }} disabled={event?.routeId != null && !allowFutureForRoute}>
              <div>This and future events</div>
              {event?.routeId != null && !allowFutureForRoute &&
                <div style={{ color: red.primary }}>This recurring event is part of {routeQuery.data?.name ?? 'a route'} and cannot be edited. You can edit this event by editing the route.</div>
              }
            </Radio>
          </div>
        </Radio.Group>
      </div>
    </Modal>
  )
}

export default ScheduledEventsChangesModal